import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { selectPrize, recordWin } from './gameUtils';

const ScratchCard = ({ clientId, prizes, setVoucher, setScreen }) => {
  const [gameKey, setGameKey] = useState(0);
  return <ScratchCardInner key={gameKey} clientId={clientId} prizes={prizes}
    setVoucher={setVoucher} setScreen={setScreen} onRetry={() => setGameKey(k => k + 1)} />;
};

const ScratchCardInner = ({ clientId, prizes, setVoucher, setScreen, onRetry }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const revealedRef = useRef(false);
  const scratchCountRef = useRef(0);
  const [prize] = useState(() => selectPrize(prizes));
  const [revealed, setRevealed] = useState(false);
  const [showLose, setShowLose] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !prize) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;

    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#0d9488');
    g.addColorStop(0.25, '#E8D48B');
    g.addColorStop(0.5, '#D4B65E');
    g.addColorStop(0.75, '#0d9488');
    g.addColorStop(1, '#0f766e');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = 'rgba(14, 30, 45, 0.06)';
    ctx.lineWidth = 1;
    for (let i = -h; i < w + h; i += 14) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i - h, h);
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(253, 248, 240, 0.35)';
    for (let i = 0; i < 30; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 1 + Math.random(), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = 'rgba(14, 30, 45, 0.6)';
    ctx.font = 'bold 22px "Cormorant Garamond", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Grattez ici !', w / 2, h / 2 - 5);
    ctx.font = '12px Manrope, sans-serif';
    ctx.fillStyle = 'rgba(14, 30, 45, 0.4)';
    ctx.fillText('utilisez votre doigt', w / 2, h / 2 + 18);

    setCanvasReady(true);
  }, [prize]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * sx, y: (e.touches[0].clientY - rect.top) * sy };
    }
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const doScratch = (x, y) => {
    if (revealedRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 26, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    scratchCountRef.current++;
    if (scratchCountRef.current % 8 === 0) checkReveal();
  };

  const checkReveal = () => {
    if (revealedRef.current) return;
    const canvas = canvasRef.current;
    const data = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 64) {
      if (data[i] === 0) transparent++;
    }
    const total = Math.ceil(data.length / 64);
    if (transparent / total > 0.45) {
      revealedRef.current = true;
      setRevealed(true);
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      handleWin();
    }
  };

  const handleWin = async () => {
    if (!prize) return;
    if (!prize.win) {
      setTimeout(() => setShowLose(true), 500);
      return;
    }
    confetti({ particleCount: 100, spread: 80, colors: ['#0d9488', 'white', '#14b8a6', '#dc2626'], origin: { y: 0.5 } });
    const data = await recordWin(clientId, prize.id, prize.label);
    if (data.win && data.voucher) {
      setTimeout(() => { setVoucher(data.voucher); setScreen('voucher'); }, 1800);
    }
  };

  const onStart = (e) => { e.preventDefault(); isDrawingRef.current = true; const p = getPos(e); doScratch(p.x, p.y); };
  const onMove = (e) => { e.preventDefault(); if (!isDrawingRef.current) return; const p = getPos(e); doScratch(p.x, p.y); };
  const onEnd = () => { isDrawingRef.current = false; checkReveal(); };

  if (!prize) return null;

  return (
    <div className="text-center" data-testid="scratch-screen" style={{ opacity: canvasReady ? 1 : 0, transition: 'opacity 0.3s' }}>
      <h2 className="text-2xl font-bold text-[#0d9488] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Grattez votre carte !
      </h2>
      <p className="text-[white]/50 text-sm mb-6">Grattez la surface doree avec votre doigt</p>

      <div className="relative inline-block rounded-2xl overflow-hidden"
        style={{ width: 300, height: 200, boxShadow: '0 0 40px rgba(201,168,76,0.15)', border: '2px solid rgba(201,168,76,0.3)' }}
        data-testid="scratch-card">
        <div className="absolute inset-0 bg-[#243559] flex flex-col items-center justify-center">
          <span className="text-5xl mb-2">{prize.emoji}</span>
          <p className={`text-lg font-bold ${prize.win ? 'text-[#0d9488]' : 'text-[white]/40'}`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {prize.win ? prize.label : 'Pas de chance...'}
          </p>
          {prize.win && <p className="text-[#14b8a6] text-xs mt-1">Vous avez gagne !</p>}
          {!prize.win && <p className="text-[white]/20 text-xs mt-1">Retentez votre chance !</p>}
        </div>

        <canvas ref={canvasRef} width={300} height={200}
          className="absolute inset-0 touch-none"
          style={{ cursor: revealed ? 'default' : 'grab', opacity: revealed ? 0 : 1, transition: 'opacity 0.4s ease' }}
          onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
          data-testid="scratch-canvas" />
      </div>

      {revealed && prize.win && (
        <p className="text-[#0d9488] text-sm mt-6 animate-pulse" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Preparation de votre bon...
        </p>
      )}

      {showLose && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="lose-dialog">
          <div className="bg-[#243559] border border-[#0d9488]/20 rounded-2xl p-8 max-w-sm w-full text-center">
            <p className="text-3xl mb-3">&#128532;</p>
            <h3 className="text-xl text-[white] font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Pas de chance !
            </h3>
            <p className="text-[white]/60 text-sm mb-6">Voulez-vous retenter votre chance ?</p>
            <div className="flex gap-3">
              <button onClick={() => setScreen('register')}
                className="flex-1 py-2.5 rounded-lg border border-[white]/20 text-[white]/70 text-sm hover:bg-[white]/5 transition"
                data-testid="lose-quit">Non merci</button>
              <button onClick={onRetry}
                className="flex-1 py-2.5 rounded-lg bg-[#0d9488] text-[#1a2744] font-bold text-sm hover:bg-[#14b8a6] transition"
                data-testid="lose-retry">Retenter !</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScratchCard;
